import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import * as fromApp from "../../store/app.reducer";
import * as AuthActions from "../../auth/store/auth.actions";
import { DataStorageService } from "../data-storage/data-storage.service";

// Instant in-app account deletion (App Store guideline 5.1.1(v)). It runs only for
// groups with groupApp.instantAccountDeletionEnabled === true. Every other group
// keeps the SMS-link request flow of the account pages, which stays unchanged.
@Injectable({
  providedIn: "root",
})
export class AccountDeletionService {
  constructor(
    private alertController: AlertController,
    private dataStorageService: DataStorageService,
    private router: Router,
    private store: Store<fromApp.AppState>,
    private translateService: TranslateService
  ) {}

  isInstantAccountDeletionEnabled(groupApp): boolean {
    return !!groupApp && groupApp.instantAccountDeletionEnabled === true;
  }

  async confirmAndDeleteAccount(
    mo_token,
    setLoading: (loading: boolean) => void,
    onDeleted?: () => void | Promise<void>
  ) {
    const alert = await this.alertController.create({
      header: this.translateService.instant("alert"),
      message: this.translateService.instant(
        "account-settings.are-you-sure-that-you-want-to-permanently-delete-account"
      ),
      buttons: [
        {
          text: this.translateService.instant("cancel"),
          role: "cancel",
          handler: () => {},
        },
        {
          text: this.translateService.instant("account-settings.delete-account"),
          role: "confirm",
          handler: () => {
            this.deleteAccount(mo_token, setLoading, onDeleted);
          },
        },
      ],
    });

    await alert.present();
  }

  private deleteAccount(
    mo_token,
    setLoading: (loading: boolean) => void,
    onDeleted?: () => void | Promise<void>
  ) {
    setLoading(true);
    this.dataStorageService
      .triggerDeleteAccount(mo_token, {
        instant_delete: true,
        language_code: this.translateService.currentLang,
      })
      .subscribe({
        next: async (res: any) => {
          setLoading(false);
          if (res && res.success && res.deleted === true) {
            const message = this.translateService.instant(
              "account-settings.your-account-has-been-deleted"
            );
            this.logout();
            if (onDeleted) {
              try {
                await onDeleted();
              } catch (error) {
                // Deletion succeeded; overlay cleanup must not hide its confirmation.
                console.error("Could not finish account-deletion profile cleanup", error);
              }
            }
            await this.presentAlert(message);
          } else if (res && res.success) {
            // The backend kept the SMS flow, e.g. the group flag was turned off
            // after this app copy loaded groupapp.json.
            await this.presentAlert(
              this.translateService.instant(
                "account-settings.soon-you-will-receive-an-sms-for-deliting-account"
              )
            );
          } else {
            await this.presentAlert(
              this.errorMessage(res && res.comment_id)
            );
          }
        },
        error: async () => {
          setLoading(false);
          await this.presentAlert(
            this.translateService.instant(
              "errorMessages.problem_reaching_server"
            )
          );
        },
      });
  }

  private async presentAlert(message: string) {
    const alert = await this.alertController.create({
      header: this.translateService.instant("alert"),
      message,
      backdropDismiss: false,
      buttons: ["OK"],
    });
    await alert.present();
    return alert;
  }

  private errorMessage(comment_id: string) {
    const key = "errorMessages." + comment_id;
    const message = comment_id ? this.translateService.instant(key) : key;
    return comment_id && message !== key
      ? message
      : this.translateService.instant("errorMessages.general_error");
  }

  // Same logout the account settings page performs.
  private logout() {
    this.store.dispatch(
      new AuthActions.SetLogoutFlagTimestamp(Date.now().toString())
    );
    this.router.navigateByUrl("/homepage", {
      replaceUrl: true,
    });
  }
}
